import * as pathToRegexp from "path-to-regexp";
import {prefixPath} from "@web2/prefix_path";

type TDataQuery<T> = {
    [P in keyof T]?: TDataQuery<T[P]> | boolean;
};

/**
 * API path
 */
export const apiPath = prefixPath("/api/", {
    account: prefixPath("users/", {
        me: "me/",
        login: "login/",
        logout: "logout/",
        password: prefixPath("password/", {
            reset: prefixPath("reset/", {
                base: "",
                confirm: "confirm/"
            })
        })
    }),
    applications: prefixPath("applications/", {
        list: prefixPath("application/", {
            base: "",
            xlsx: "xlsx/"
        }),
        stats: "stats/",
        edit: ":id([\\w-]+)/"
    }),
    contractors: prefixPath("contractors/", {
        agent: "agent/"
    })
});

export const apiLink = {
    account: {
        me: createLink(apiPath.account.me),
        login: createLink(apiPath.account.login),
        logout: createLink(apiPath.account.logout),
        passwordReset: createLink(apiPath.account.password.reset.base),
        passwordResetConfirm: createLink(apiPath.account.password.reset.confirm)
    },
    applications: {
        list: createLink(apiPath.applications.list.base),
        stats: createLink(apiPath.applications.stats),
        edit: createLink<IApiApplicationEditParams>(apiPath.applications.edit),
        xlsx: createLink(apiPath.applications.list.xlsx)
    },
    contractors: {
        agentList: createLink(apiPath.contractors.agent)
    }
};

/**
 * Params Interfaces
 */

interface IApiApplicationEditParams {
    id: number;
}

/**
 * Helper
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
function createLink<TParams = null>(apiPathElement: string) {
    const compiler = pathToRegexp.compile(apiPathElement);
    return <TQuery>(dataQuery: TDataQuery<TQuery>) => {
        const includeParams = appendIncludeParams("", dataQuery);
        return (apiPathParams: TParams): string => compiler(apiPathParams as unknown as Record<string, any>) + includeParams;
    };
}

const appendIncludeParams = <T>(pathname: string, dataQuery: TDataQuery<T>): string => {
    if (isEmpty(dataQuery)) {
        return pathname;
    }

    const includes: string[] = [];
    const addParams = (query: Record<string, any>, parentKey?: string) => {
        const prefix = parentKey ? `${parentKey}.` : "";

        if (parentKey && isEmpty(query)) {
            // set default when keys are not defined
            return includes.push(`i[]=${prefix}*`);
        }

        // keys are defined - append them to array
        Object.keys(query).forEach((key: string) => {
            const currentKey = prefix + key;
            if (isObject(query[key])) {
                // we have deep object
                return addParams(query[key] as Record<string, any>, currentKey);
            } else if (query[key]) {
                // we have boolean value - add when truthly
                return includes.push(`i[]=${currentKey}`);
            }
        });
    };

    addParams(dataQuery); // fills `includes` array
    return `${pathname}?${includes.join("&")}`;
};

const isEmpty = (obj: Record<any, any>) => Object.keys(obj).length === 0 && obj.constructor === Object;
const isObject = (obj: Record<any, any> | boolean) => typeof obj === "object" && obj !== null;
